import { routes } from '@aline/routes';
import { matchPath, useLocation } from 'react-router-dom';

interface ChatView {
  view: 'chat';
}

interface CollaboratorsView {
  view: 'collaborators';
  workflowId: UUID;
}

interface CommentsView {
  view: 'comments';
  workflowId: UUID;
}

interface DocumentsView {
  view: 'documents';
  workflowId: UUID;
}

interface IntakeView {
  view: 'intake';
  workflowId: UUID;
}

interface PhaseSettingsView {
  phaseId: UUID;
  view: 'phase-settings';
  workflowId: UUID;
}

interface TaskView {
  taskId: UUID;
  view: 'task';
  workflowId: UUID;
}

interface WorkflowSettingsView {
  view: 'workflow-settings';
  workflowId: UUID;
}

export type SidePanelState =
  | ChatView
  | CollaboratorsView
  | CommentsView
  | DocumentsView
  | IntakeView
  | PhaseSettingsView
  | TaskView
  | WorkflowSettingsView;

export type SidePanelView = SidePanelState['view'];

/**
 * Derives the side-panel view from the current pathname.
 * Uses matchPath + useLocation (not useMatch) so it works when the component
 * is rendered outside the workflow Routes (e.g. in the global right drawer).
 */
export function useSidePanelView(): SidePanelState {
  const location = useLocation();
  const pathname = location.pathname;

  const matchTask = matchPath(routes.WorkflowDetailTabTask, pathname);
  const matchPhaseSettings = matchPath(routes.WorkflowDetailTabPhaseSettings, pathname);
  const matchOptions = matchPath(routes.WorkflowDetailTabOptions, pathname);
  const matchIntake = matchPath(routes.WorkflowDetailTabIntake, pathname);
  const matchDocuments = matchPath(routes.WorkflowDetailTabDocuments, pathname);
  const matchComments = matchPath(routes.WorkflowDetailTabComments, pathname);
  const matchCollaborators = matchPath(routes.WorkflowDetailTabCollaborators, pathname);
  const matchTemplateDocuments = matchPath(routes.TemplateSelectedTemplateDocuments, pathname);
  const matchTemplateComments = matchPath(routes.TemplateSelectedTemplateComments, pathname);
  const matchTemplateTeam = matchPath(routes.TemplateSelectedTemplateTeam, pathname);

  if (matchTask?.params?.taskId && matchTask.params.workflowId) {
    return {
      taskId: matchTask.params.taskId,
      view: 'task',
      workflowId: matchTask.params.workflowId,
    };
  }

  if (matchPhaseSettings?.params?.phaseId && matchPhaseSettings.params.workflowId) {
    return {
      phaseId: matchPhaseSettings.params.phaseId,
      view: 'phase-settings',
      workflowId: matchPhaseSettings.params.workflowId,
    };
  }

  if (matchOptions?.params?.workflowId) {
    return { view: 'workflow-settings', workflowId: matchOptions.params.workflowId };
  }

  if (matchIntake?.params?.workflowId) {
    return { view: 'intake', workflowId: matchIntake.params.workflowId };
  }

  const documentsWorkflowId =
    matchDocuments?.params?.workflowId ?? matchTemplateDocuments?.params?.workflowId;
  if (documentsWorkflowId) return { view: 'documents', workflowId: documentsWorkflowId };

  const commentsWorkflowId =
    matchComments?.params?.workflowId ?? matchTemplateComments?.params?.workflowId;
  if (commentsWorkflowId) return { view: 'comments', workflowId: commentsWorkflowId };

  const collaboratorsWorkflowId =
    matchCollaborators?.params?.workflowId ?? matchTemplateTeam?.params?.workflowId;
  if (collaboratorsWorkflowId)
    return { view: 'collaborators', workflowId: collaboratorsWorkflowId };

  return { view: 'chat' };
}
